import { useEffect, useState } from 'react';

import { getServiceOpenApi } from 'api/documentation';

import { ApiOperation } from './types';
import { API_SERVICES, publicOperations } from './utils';

export type ApiServiceState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; operations: ApiOperation[] };

// Specs are a few hundred KB each and change only on deploy: load each once
// per page load, and only once someone opens the API reference.
const cache = new Map<string, Promise<ApiOperation[]>>();

const loadService = (service: string): Promise<ApiOperation[]> => {
  if (!cache.has(service))
    cache.set(
      service,
      getServiceOpenApi(service)
        .then(spec => publicOperations(spec, service))
        .catch(error => {
          // Let a later visit retry instead of caching the failure.
          cache.delete(service);
          throw error;
        }),
    );
  return cache.get(service)!;
};

/** Public operations per service, read live from each service's spec. */
export const useApiReference = (
  enabled: boolean,
): Record<string, ApiServiceState> => {
  const [state, setState] = useState<Record<string, ApiServiceState>>({});

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    API_SERVICES.forEach(({ id }) => {
      setState(current =>
        current[id]?.status === 'ready'
          ? current
          : { ...current, [id]: { status: 'loading' } },
      );
      loadService(id)
        .then(operations => {
          if (active)
            setState(current => ({
              ...current,
              [id]: { status: 'ready', operations },
            }));
        })
        .catch(() => {
          if (active)
            setState(current => ({ ...current, [id]: { status: 'error' } }));
        });
    });
    return () => {
      active = false;
    };
  }, [enabled]);

  return state;
};

/** For tests: forget loaded specs. */
export const resetApiReferenceCache = () => cache.clear();
